import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  FluentProvider,
  Title1,
  Text,
  Avatar,
  webLightTheme,
  webDarkTheme,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
} from '@fluentui/react-components';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import './App.css';
import { GridDotsRegular, SignOutRegular } from '@fluentui/react-icons';
import AuthGuard from './components/AuthGuard';
import RoleGuard from './components/RoleGuard';
import { AppInsightsErrorBoundary } from './components/AppInsightsErrorBoundary';
import { SideNav } from './components/SideNav';
import { Footer } from './components/Footer';
import { PartnerAliasProvider } from './contexts/PartnerAliasContext';
import { useAppLayoutStyles } from './styles/AppLayout.styles';
import { ALL_ROUTES } from './constants/appRoutes';
import { useRouteAccess } from './hooks/useRouteAccess';
import { usePrefersDarkTheme } from './hooks/usePrefersDarkTheme';
import { appInsightsReactPlugin } from './services/telemetry';

function AppContent() {
  const styles = useAppLayoutStyles();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { defaultRoute } = useRouteAccess();
  const landingPath = defaultRoute?.path ?? '/monthlyreviews';
  const isDarkTheme = usePrefersDarkTheme();
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);

  const userName = accounts?.[0]?.name || 'User';

  return (
    <FluentProvider theme={isDarkTheme ? webDarkTheme : webLightTheme}>
      <PartnerAliasProvider>
      <div className={styles.container}>
        {/* Header */}
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <GridDotsRegular className={styles.headerIcon} aria-hidden="true" tabIndex={-1} />
            <Title1 className={styles.headerTitle}>Surface Partner Program Portal​</Title1>
          </div>
          <div className={styles.headerRight}>
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Button
                  appearance="transparent"
                  className={styles.headerUserButton}
                >
                  <Avatar name={userName} size={32} />
                  {'\u00A0'}
                  <Text className={styles.headerUserName}>{userName}</Text>
                </Button>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={<SignOutRegular />}
                    onClick={() => instance.logoutRedirect()}
                  >
                    Sign out
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          </div>
        </header>

        <div className={styles.body}>
          <SideNav
            currentPath={location.pathname}
            onNavigate={(path) => navigate(path)}
            isOpen={isSideNavOpen}
            onToggle={() => setIsSideNavOpen((prev) => !prev)}
          />

          <main className={styles.content}>
            <div className={styles.contentRoutes}>
              <Routes>
                <Route path="/" element={<Navigate to={landingPath} replace />} />
                {ALL_ROUTES.map((route) => (
                  <Route
                    key={route.path}
                    path={route.path}
                    element={<RoleGuard roles={route.roles}>{route.element}</RoleGuard>}
                  />
                ))}
                <Route path="*" element={<Navigate to={landingPath} replace />} />
              </Routes>
            </div>
            <Footer />
          </main>
        </div>
      </div>
      </PartnerAliasProvider>
    </FluentProvider>
  );
}

function App() {
  return (
    <AppInsightsContext.Provider value={appInsightsReactPlugin}>
      <AppInsightsErrorBoundary>
        <AuthGuard>
          <AppContent />
        </AuthGuard>
      </AppInsightsErrorBoundary>
    </AppInsightsContext.Provider>
  );
}

export default App;
